import { useRef } from 'react';

import EBTCardSummary from 'features/shop/cart/EBTPaymentFlow/shared/EBTCardSummary';
import EBTPaymentForm from 'features/shop/cart/EBTPaymentFlow/shared/EBTPaymentForm';
import { StepButton, StepCard, StepHeading } from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import useEBTPaymentCapture from 'features/shop/cart/EBTPaymentFlow/shared/useEBTPaymentCapture';
import { EBTFormSubmissionError } from 'shared/ebt/EBTErrors';
import { EBTCardLast4 } from 'shared/ebt/EBTSharedUI';
import { trackEvent } from 'utils/analyticsUtils';

/**
 * EditEBTPaymentConfirm - submits the edited payment
 *
 * @param {Object} props - Component props
 * @param {Object} props.cartData - Cart data containing SNAP totals
 * @param {number} props.cartData.snapEligibleTotal - SNAP-eligible amount
 * @param {number} props.cartData.snapIneligibleTotal - SNAP ineligible amount
 * @param {number} props.currentAmount - The current EBT payment amount that will be modified
 * @param {Object} props.ebtCard - EBT card information containing last4 digits
 * @param {string} props.ebtCard.last4 - Last 4 digits of the EBT card
 * @param {Object} props.flowData - Flow state data containing payment amounts
 * @param {number} props.flowData.newAmount - New EBT payment amount to be processed
 * @param {Function} props.handleNext - Callback to navigate to the next step in the flow
 * @param {boolean} props.isStepLoading - Loading state for the current step
 * @param {Function} props.setDisableClose - Function to control modal close behavior
 * @param {Function} props.setFlowData - Function to update flow state data
 * @param {Function} props.setIsStepLoading - Function to update step loading state
 *
 * @returns {JSX.Element} The rendered confirmation step component
 */

function EditEBTPaymentConfirm({
    cartData,
    currentAmount: initialCurrentAmount,
    ebtCard,
    flowData,
    handleNext,
    isStepLoading,
    setDisableClose,
    setFlowData,
    setIsStepLoading,
}) {
    const { captureEBTPayment, clearEBTPaymentCaptureError, ebtPaymentCaptureError, isCapturingEBTPayment } =
        useEBTPaymentCapture();

    const { newAmount } = flowData;

    const currentAmount = useRef(initialCurrentAmount).current;

    const isRefund = newAmount < currentAmount;
    const refundAmount = currentAmount - newAmount;

    const handleSubmit = async (formData) => {
        setIsStepLoading(true);
        setDisableClose(true);
        clearEBTPaymentCaptureError();

        try {
            const response = await captureEBTPayment({
                cartData,
                ebtAmount: newAmount,
                pinElement: formData?.pinElement, // pinElement only exists in charge flow
                previousAmount: currentAmount,
            });

            const eventProperties = {
                SNAP_eligible_amount: cartData?.snapEligibleTotal || 0,
                SNAP_ineligible_amount: cartData?.snapIneligibleTotal || 0,
                SNAP_payment_amount: Number(newAmount),
                SNAP_previous_payment_amount: Number(currentAmount),
            };

            // Determine type: increase, decrease, or remove
            if (Number(newAmount) === 0) {
                eventProperties.type = 'remove';
            } else if (Number(newAmount) > Number(currentAmount)) {
                eventProperties.type = 'increase';
            } else {
                eventProperties.type = 'decrease';
            }

            trackEvent('SNAP payment update', eventProperties);

            setFlowData({ ...flowData, isRefund, refundAmount, apiResponse: response });
            handleNext('EditEBTPaymentSuccess');
        } finally {
            setIsStepLoading(false);
            setDisableClose(false);
        }
    };

    return (
        <div className="relative space-y-15">
            <StepHeading>Edit EBT Amount</StepHeading>
            <div className="grid grid-cols-2 gap-10">
                <div className="border border-grey-light p-10" data-testid="ebt-current-amount">
                    <p>Previous EBT Amount:</p>
                    <p>
                        <span className="font-grotesk-bold">${currentAmount?.toFixed(2)}</span>
                    </p>
                </div>
                <div className="border border-grey-light p-10" data-testid="ebt-new-amount">
                    <p>New EBT Amount:</p>
                    <p className="flex items-center gap-10">
                        <span className="text-brand-primary font-grotesk-bold">${newAmount?.toFixed(2)}</span>
                        <button
                            type="button"
                            className="link"
                            onClick={() => handleNext('EditEBTPaymentAmount', { focusAmountField: true })}
                        >
                            Edit
                        </button>
                    </p>
                </div>
            </div>
            {isRefund ? (
                <>
                    <p className="text-center">
                        Your <EBTCardLast4 last4={ebtCard?.last4} /> will be refunded{' '}
                        <span className="text-brand-primary font-grotesk-bold">${refundAmount?.toFixed(2)}</span>.
                    </p>
                    <StepButton onClick={() => handleSubmit()} disabled={isStepLoading}>
                        Confirm Refund
                    </StepButton>
                    {ebtPaymentCaptureError && (
                        <EBTFormSubmissionError className="space-y-5 text-center" error={ebtPaymentCaptureError} />
                    )}
                </>
            ) : (
                <StepCard className="space-y-15">
                    <EBTCardSummary last4={ebtCard?.last4} />
                    <p className="text-center">
                        Your previous amount of <span className="font-grotesk-bold">${currentAmount?.toFixed(2)}</span>{' '}
                        will be refunded, and your new total of{' '}
                        <span className="text-brand-primary font-grotesk-bold">${newAmount?.toFixed(2)}</span> will be
                        charged.
                    </p>
                    <EBTPaymentForm
                        error={ebtPaymentCaptureError}
                        isAmountFieldShown={false}
                        isPaymentProcessing={isCapturingEBTPayment}
                        onSubmit={handleSubmit}
                        pinElementID="edit-charge-pin-element"
                        submitButtonText="Confirm Charge"
                    />
                </StepCard>
            )}
        </div>
    );
}

export default EditEBTPaymentConfirm;
